import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, makeStyles, Popover, InputAdornment } from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import _ from 'lodash';
import moment from 'moment';
import { TELEVISIT_ROBOT_STATUS, TELEVISIT_STATUS, TELEVISIT_STYLE } from '../../../comm/constant';
import { DateIcon, DropLeftIcon, DropRightIcon } from '../../../components/Icon';
import emptyBg from '../../../static/images/emptyBg.svg';
import StyleButton from '../../../components/StyleButton';
import useResize from '../../../utils/useResize';
import { classNames } from '../../../utils';
import { ReactComponent as RobotBg } from '../../../static/images/unavailableRobotBG.svg';
import { i18n } from '../../../language';
import TimeslotCard from './TimeslotCard';

const useStyle = makeStyles((theme) => ({
  date: ({ width, color }) => ({
    '& .MuiInputBase-root': {
      borderRadius: '0.5rem',
      backgroundColor: '#538fff',
      border: 'unset',
      marginRight: '0.5rem',
    },
    '& .MuiInputBase-input': {
      fontFamily: 'NotoSans-Bold',
      fontSize: '1.5rem',
      letterSpacing: '0',
      color: color ?? theme.palette.font.white,
      fontWeight: '500',
      // width: '100%',
      maxWidth: '20rem',
      padding: '0',
      display: 'none',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'unset',
    },
    '& .MuiIconButton-root': {
      padding: '0',
    },
  }),
  dateIcon: {
    color: theme.palette.font.white,
  },
  periodHeader: {
    position: 'sticky',
    top: 0,
    height: '2.5rem',
    zIndex: 9999,
    backgroundColor: '#fff',
  },
  periodBox: ({ periodMarginTop }) => ({
    width: '5rem',
    flexDirection: 'column',
    zIndex: 100,
    backgroundColor: '#ffffff',
    '&>div:nth-child(2)': {
      marginTop: periodMarginTop,
    },
  }),
  period: ({ periodHeight }) => ({
    marginTop: periodHeight,
    lineHeight: '1.5rem',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#aaaaaa',
    padding: '0 1rem',
  }),
  title: {
    height: '2.5rem',
    position: 'sticky',
    top: 0,
    zIndex: 99,
    backgroundColor: '#ffffff',
    fontFamily: 'NotoSans-Bold',
    fontSize: '1rem',
    color: '#2e4469',
    paddingTop: '0.7rem',
  },
  outOfService: {
    color: 'rgb(46,68,105,0.5) !important',
  },
  robotBox: {
    minWidth: '25rem',
    position: 'relative',
    '&>div:nth-child(2)': {
      marginTop: '0',
    },
  },
  lineBox: ({ periodHeight }) => ({
    marginTop: periodHeight,
    width: '100%',
    height: '1.5rem',
    display: 'flex',
    alignItems: 'center',
  }),
  line: {
    height: '0.063rem',
    backgroundColor: '#eeeeee',
    width: '100%',
  },
  timeLine: () => ({
    width: `calc('100%' - 55px)`,
    translate: '55px',
    height: '0.125rem',
    backgroundColor: '#538fff',
    position: 'absolute',
    top: '0',
    left: '0',
    zIndex: '100',
    pointerEvents: 'none',
    '&::before': {
      position: 'absolute',
      top: '0',
      left: '0',
      content: '""',
      display: 'block',
      width: '0.5rem',
      height: '0.5rem',
      backgroundColor: '#538fff',
      borderRadius: '50%',
      transform: 'translate(0, -0.2rem)',
      zIndex: '1',
    },
  }),
  unRobotBg: {
    position: 'sticky',
    bottom: 0,
    right: 0,
    zIndex: 9,
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
  },
  newDateText: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.5rem',
    color: theme.palette.font.white,
  },
}));

const periodHeight = '4rem';

const handleInitUnavailableRobotTodaySchedule = ({ scheduledItem, timeState }) => {
  let result = {
    display: true,
    style: {},
    click: true,
  };

  switch (scheduledItem.timeslotBookingStatusEnum) {
    case TELEVISIT_STATUS.UNAVAILABLE:
      result.display = false;
      break;
    case TELEVISIT_STATUS.AVAILABLE:
      result.display = false;
      break;
    case TELEVISIT_STATUS.RESERVED:
      result.style = {
        ...TELEVISIT_STYLE[scheduledItem.timeslotBookingStatusEnum],
        opacity: moment(timeState, 'HH:mm').isBefore(moment(scheduledItem.endTime, 'HH:mm')) ? 1 : 0.5,
      };
      break;
    case TELEVISIT_STATUS.CONFIRMED:
    case TELEVISIT_STATUS.INPROGRESS:
    case TELEVISIT_STATUS.COMPLETED:
    case TELEVISIT_STATUS.COMPLETED_MANUALLY:
    case TELEVISIT_STATUS.ROBOT_START_PROCESSING_BOOKING:
      if (moment(timeState, 'HH:mm').isBefore(moment(scheduledItem.startTime, 'HH:mm'))) {
        result.style = TELEVISIT_STYLE[TELEVISIT_STATUS.CONFIRMED];
      } else if (moment(timeState, 'HH:mm').isAfter(moment(scheduledItem.endTime, 'HH:mm'))) {
        result.style = TELEVISIT_STYLE[TELEVISIT_STATUS.COMPLETED];
      } else {
        result.style = TELEVISIT_STYLE[TELEVISIT_STATUS.INPROGRESS];
      }
      break;
    default:
      break;
  }

  return result;
};

const handleInitUnavailableRobotSchedule = ({ scheduledItem, timeState }) => {
  let result = {
    display: true,
    style: {},
    click: true,
  };

  switch (scheduledItem.timeslotBookingStatusEnum) {
    case TELEVISIT_STATUS.UNAVAILABLE:
    case TELEVISIT_STATUS.AVAILABLE:
      result.display = false;
      break;
    default:
      result.style = {
        ...TELEVISIT_STYLE[scheduledItem.timeslotBookingStatusEnum],
        opacity: moment(moment(scheduledItem.day, 'x')).isBefore(moment()) ? 0.5 : 1,
      };
      break;
  }

  return result;
};
const handleInitAvailableRobotTodaySchedule = ({ scheduledItem, timeState }) => {
  let result = {
    display: true,
    style: {},
    click: true,
  };

  switch (scheduledItem.timeslotBookingStatusEnum) {
    case TELEVISIT_STATUS.UNAVAILABLE:
      if (scheduledItem.bookId) {
        result.display = false;
      } else if (moment(timeState, 'HH:mm').isBefore(moment(scheduledItem.endTime, 'HH:mm'))) {
        result.style = {
          ...TELEVISIT_STYLE[scheduledItem.timeslotBookingStatusEnum],
        };
      } else {
        result.display = false;
      }

      break;
    case TELEVISIT_STATUS.AVAILABLE:
      if (moment(timeState, 'HH:mm').isBefore(moment(scheduledItem.endTime, 'HH:mm'))) {
        result.style = {
          ...TELEVISIT_STYLE[scheduledItem.timeslotBookingStatusEnum],
        };
      } else {
        result.display = false;
      }
      break;
    case TELEVISIT_STATUS.RESERVED:
      result.style = {
        ...TELEVISIT_STYLE[scheduledItem.timeslotBookingStatusEnum],
        opacity: moment(timeState, 'HH:mm').isBefore(moment(scheduledItem.endTime, 'HH:mm')) ? 1 : 0.5,
      };
      break;
    case TELEVISIT_STATUS.CONFIRMED:
    case TELEVISIT_STATUS.INPROGRESS:
    case TELEVISIT_STATUS.COMPLETED:
    case TELEVISIT_STATUS.COMPLETED_MANUALLY:
    case TELEVISIT_STATUS.ROBOT_START_PROCESSING_BOOKING: {
      result.style = getStyleForConfirmed(scheduledItem, timeState);
      break;
    }
    default:
      break;
  }
  return result;
};

const getStyleForConfirmed = (scheduledItem, timeState) => {
  let style;
  let endTime = moment(scheduledItem.endTime, 'HH:mm');
  let startTime = moment(scheduledItem.startTime, 'HH:mm');
  if (scheduledItem.hocFlag === 1 && startTime.isAfter(endTime)) {
    if (scheduledItem.isMainBooking === 1) {
      endTime = endTime.add(1, 'd');
    }
    if (scheduledItem.isMainBooking === 0) {
      startTime = startTime.add(-1, 'd');
    }
  }

  if (moment(timeState, 'HH:mm').isBefore(startTime)) {
    style =
      scheduledItem.hocFlag === 1 && scheduledItem.bookId
        ? TELEVISIT_STYLE[TELEVISIT_STATUS.ADHOC_CONFIRMED]
        : TELEVISIT_STYLE[TELEVISIT_STATUS.CONFIRMED];
  } else if (moment(timeState, 'HH:mm').isAfter(endTime)) {
    style =
      scheduledItem.hocFlag === 1 && scheduledItem.bookId
        ? TELEVISIT_STYLE[TELEVISIT_STATUS.ADHOC_COMPLETED]
        : TELEVISIT_STYLE[TELEVISIT_STATUS.COMPLETED];
    scheduledItem.timeslotBookingStatusEnum = TELEVISIT_STATUS.COMPLETED;
  } else {
    style = TELEVISIT_STYLE[TELEVISIT_STATUS.INPROGRESS];
  }
  return style;
};
const handleInitAvailableRobotSchedule = ({ scheduledItem }) => {
  let result = {
    display: true,
    style: {},
    click: true,
  };

  let day = scheduledItem.isMainBooking === 0 ? 1 : 0;
  const adhHocStyle = {
    ...TELEVISIT_STYLE[TELEVISIT_STATUS.ADHOC_CONFIRMED],
    opacity: moment(moment(scheduledItem.day, 'x').add(day, 'd')).isBefore(moment()) ? 0.5 : 1,
  };
  result.style =
    scheduledItem.hocFlag === 1 && scheduledItem.bookId
      ? adhHocStyle
      : {
          ...TELEVISIT_STYLE[scheduledItem.timeslotBookingStatusEnum],
          opacity: moment(moment(scheduledItem.day, 'x')).isBefore(moment()) ? 0.5 : 1,
        };

  return result;
};

const Schedule = ({ timeslotList, time, handleTime, handleDetail, endDate, scrollLocation }) => {
  const periodMarginTop = useMemo(() => {
    return _.find(timeslotList?.data, (item) => {
      return item.isMainBooking === 0;
    })
      ? 40
      : 0;
  }, [timeslotList]);

  const classes = useStyle({ periodHeight, periodMarginTop });
  const ref = useRef();
  const resize = useResize();
  const periodMemo = useMemo(() => {
    let tempTime = '00:00';
    let periods = ['00:00'];

    while (moment(tempTime, 'HH:mm').isBefore(moment('23:59', 'HH:mm'))) {
      if (tempTime === '23:30') {
        tempTime = moment(tempTime, 'HH:mm').add(29, 'm').format('HH:mm');
      } else {
        tempTime = moment(tempTime, 'HH:mm').add(30, 'm').format('HH:mm');
      }

      periods.push(tempTime);
    }

    return periods;
  }, []);
  const [refresh, setRefresh] = useState(true);
  const [timelineFlag, setTimelineFlag] = useState(false);
  const [timeState, setTimeState] = useState(moment().format('HH:mm'));
  const [datepickerRef, setDatepickerRef] = useState(null);
  const [dateViewOpen, setDateViewOpen] = useState(false);

  const robotMemo = useMemo(() => {
    let timeslot = _.map(timeslotList.robotData, (item, index) => {
      return {
        name: item.robotName,
        robotStatus: item.robotStatus,
        schedules: _.map(
          _.filter(timeslotList.data, (timeslotItem) => timeslotItem.robotId === item.robotId),
          (scheduledItem) => {
            let stateData;

            if (scheduledItem.robotStatusEnum === TELEVISIT_ROBOT_STATUS.UNAVAILABLE) {
              // robot unavailable
              if (timelineFlag) {
                // current day
                stateData = handleInitUnavailableRobotTodaySchedule({ scheduledItem, timeState });
              } else {
                // not current day
                stateData = handleInitUnavailableRobotSchedule({ scheduledItem, timeState });
              }
            }
            // robot available
            else if (timelineFlag) {
              // current day
              stateData = handleInitAvailableRobotTodaySchedule({ scheduledItem, timeState });
            } else {
              // not current day
              stateData = handleInitAvailableRobotSchedule({ scheduledItem });
            }

            return {
              ...scheduledItem,
              status: scheduledItem.robotStatusEnum,
              scheduleId: scheduledItem.bedNo,
              startTime: scheduledItem.startTime,
              endTime: scheduledItem.endTime,
              organizer: [scheduledItem.patientNameEn, scheduledItem.patientNameCh].filter((item) => item).join(', '),
              ...stateData,
            };
          }
        ),
        key: index,
      };
    });

    return timeslot;
  }, [timeslotList, timeState, timelineFlag]);

  // timeLine location
  useEffect(() => {
    if (timelineFlag && ref?.current) {
      const timeLine = document.getElementById('timeLine');
      let startingPoint =
        document.getElementById('00:00')?.offsetTop + document.getElementById('00:00')?.offsetHeight / 2;
      const minuteHeight =
        (document.getElementById('00:30')?.offsetTop - document.getElementById('00:00')?.offsetTop) / 30;
      const minutes = Math.floor((moment().valueOf() - moment(periodMemo[0], 'hh:mm').valueOf()) / (1000 * 60));

      timeLine.style.width = `calc(${ref.current.scrollWidth + 15}px - 5rem)`;
      timeLine.style.top = `${minutes * minuteHeight + startingPoint}px`;
      setTimeState(moment().format('HH:mm'));
    }
  }, [ref?.current, resize, timelineFlag, refresh, periodMemo, robotMemo, periodMarginTop]);

  // Refresh every minute
  useEffect(() => {
    if (moment(time).isSame(moment(), 'd')) {
      const start = moment().isAfter(moment(periodMemo[0], 'hh:mm'));
      setTimelineFlag(start);

      const interval = setInterval(() => {
        setRefresh((pre) => !pre);
        if (moment().isBetween(moment(periodMemo[0], 'hh:mm'), moment(periodMemo[periodMemo.length - 1], 'hh:mm'))) {
          setTimelineFlag(true);
        } else {
          setTimelineFlag(false);
        }
      }, 60 * 1000);

      return () => {
        clearInterval(interval);
      };
    } else {
      setTimelineFlag(false);
    }
  }, [periodMemo, time]);

  // Automatically scroll to the position of the timeline
  useEffect(() => {
    const table = document.getElementById('timeslot-table');
    const today = moment().isSame(time, 'D');
    if (today) {
      const hour = moment().hour() < 10 ? `0${moment().hour()}` : moment().hour();
      const periodEl = document.getElementById(`${hour}:${moment().minute() / 30 > 1 ? '30' : '00'}`);
      if (periodEl) {
        table.scrollTo({
          left: 0,
          top: periodEl.offsetTop - 30,
          behavior: 'smooth',
        });
      }
    } else {
      const { scrollTop } = ref.current;
      if (scrollLocation.current === 0) {
        let timeslots = document.getElementsByName('TimeSlot_Card');
        if (timeslots.length > 0) {
          let sortTimeslots = _.sortBy(timeslots, 'offsetTop');
          sortTimeslots[0].scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        }
        scrollLocation.current = scrollTop;
      }
    }
  }, [timeslotList]);

  const openDateView = useCallback((event) => {
    setDateViewOpen(true);
    setDatepickerRef(event.currentTarget);
  }, []);

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        bgcolor="#538fff"
        borderRadius="10px 10px 0 0"
        p="0.625rem 1.25rem"
      >
        <Box display="flex" alignItems="center">
          <Box className={classes.newDateText}>{moment(time).format('dddd, YYYY-MM-DD')}</Box>
          <DatePicker
            autoOk
            disabled
            className={classes.date}
            size="small"
            onChange={handleTime('choose')}
            value={time}
            variant="inline"
            format="dddd, YYYY-MM-DD"
            inputVariant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <ButtonBase onClick={openDateView}>
                    <DateIcon className={classes.dateIcon} />
                  </ButtonBase>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Popover
          open={dateViewOpen}
          onClose={() => setDateViewOpen(false)}
          anchorEl={datepickerRef}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <DatePicker
            autoOk
            size="small"
            onChange={(props) => {
              handleTime('choose')(props);
              setDateViewOpen(false);
            }}
            value={time}
            variant="static"
            format="dddd, YYYY-MM-DD"
            inputVariant="outlined"
            maxDate={endDate}
          />
        </Popover>
        <Box display="flex" gridGap="0.625rem">
          <StyleButton
            onClick={handleTime('today')}
            text={i18n.t('TELEVISIT_TODAY')}
            color="#333333"
            width="5.375rem"
            height="2.563rem"
          />
          <StyleButton
            onClick={handleTime('reduce')}
            Icon={DropLeftIcon}
            width="2.563rem"
            height="2.563rem"
            iconStyle={{ fontSize: '0.75rem' }}
          />
          <StyleButton
            onClick={handleTime('add')}
            Icon={DropRightIcon}
            width="2.563rem"
            height="2.563rem"
            iconStyle={{ fontSize: '0.75rem' }}
            disabled={moment(time).isSame(endDate, 'D')}
          />
        </Box>
      </Box>

      <Box
        id="timeslot-table"
        overflow="auto"
        width="100%"
        height="100%"
        border="solid 1px #e4e7eb"
        borderTop="unset"
        pb="2rem"
        borderRadius="0 0 10px 10px"
        ref={ref}
      >
        <Box display="flex" position="relative">
          <Box className={classes.periodBox}>
            <div className={classes.periodHeader} />
            {periodMemo.map((item) => (
              <div className={classes.period} id={item} key={item}>
                {item}
              </div>
            ))}
          </Box>

          <Box display="flex" gridGap="0.5rem">
            {robotMemo.map((item) =>
              item.name ? (
                <Box id="schedule-body" className={classes.robotBox} key={item.key}>
                  {item.robotStatus === TELEVISIT_ROBOT_STATUS.AVAILABLE ? (
                    <div className={classes.title}>{item.name}</div>
                  ) : (
                    <div className={classNames(classes.title, classes.outOfService)}>{`${item.name} ${i18n.t(
                      'TELEVISIT_OUT_OF_SERVICE'
                    )}`}</div>
                  )}
                  {periodMemo.map((periodItem, periodIndex) => (
                    <div className={classes.lineBox} key={periodItem}>
                      <div id={`line${periodIndex}`} className={classes.line} />
                    </div>
                  ))}
                  {item.schedules.map((scheduleItem, scheduleIndex) => (
                    <TimeslotCard
                      info={scheduleItem}
                      periodMemo={periodMemo}
                      key={
                        scheduleItem?.bookId ??
                        `${scheduleItem.startTime} - ${scheduleItem.endTime}, ${scheduleIndex}, ${scheduleItem.robotStatusEnum}`
                      }
                      handleDetail={handleDetail}
                      periodMarginTop={periodMarginTop}
                    />
                  ))}
                </Box>
              ) : (
                <Box
                  id="schedule-body"
                  className={classes.robotBox}
                  style={{ backgroundImage: `url(${emptyBg})`, backgroundColor: '#f1f6ff' }}
                  key={item.key}
                >
                  <div className={classes.title} style={{ color: '#c0c6cb' }} />
                  {periodMemo.map((periodItem, periodIndex) => (
                    <div className={classes.lineBox} key={periodItem}>
                      <div id={`line${periodIndex}`} className={classes.line} />
                    </div>
                  ))}
                  {item.schedules.map((scheduleItem, scheduleIndex) => (
                    <TimeslotCard
                      info={scheduleItem}
                      periodMemo={periodMemo}
                      key={
                        scheduleItem?.bookId ??
                        `${scheduleItem.startTime} - ${scheduleItem.endTime}, ${scheduleIndex}, ${scheduleItem.robotStatusEnum}`
                      }
                      handleDetail={handleDetail}
                      periodMarginTop={periodMarginTop}
                    />
                  ))}
                  <Box className={classes.unRobotBg}>
                    <RobotBg />
                  </Box>
                </Box>
              )
            )}
          </Box>
          {timelineFlag && <div id="timeLine" className={classes.timeLine} />}
        </Box>
      </Box>
    </>
  );
};

export default Schedule;
